import { Button, Card, Container, Row, Col, Nav } from "react-bootstrap";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { editOrder, fetchOrders } from "../../../Redux/ordersReduce";

function ListOrders() {
  const [entities, setEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loadingStates, setLoadingStates] = useState({});
  const dispatch = useDispatch();
  const navigate = useHistory();

  // Status constants
  const STATUS_TABS = [
    { id: "all", label: "Toutes" },
    { id: "En attente", label: "En attente" },
    { id: "En cours de traitement", label: "En cours de traitement" },
    { id: "Expédié", label: "Expédié" },
    { id: "Terminé", label: "Terminé" },
    { id: "Annuler", label: "Annuler" },
  ];

  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i>
          {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i>
          {msg}
        </strong>,
      );
  };

  // Filter orders based on active tab
  useEffect(() => {
    if (activeTab === "all") {
      setFilteredEntities(entities);
    } else {
      setFilteredEntities(
        entities.filter((order) => order.status === activeTab),
      );
    }
  }, [activeTab, entities]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      await dispatch(editOrder({ id: orderId, status: newStatus })).unwrap();
      notify(
        1,
        `Statut de la commande mis à jour en ${translateStatus(newStatus)}`,
      );
      getOrders(); // Refresh orders list
    } catch (error) {
      notify(2, error.message || "Échec de la mise à jour du statut");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = [
      "En attente",
      "En cours de traitement",
      "Expédié",
      "Terminé",
    ];
    const currentIndex = statusFlow.indexOf(currentStatus);
    return currentIndex < statusFlow.length - 1
      ? statusFlow[currentIndex + 1]
      : currentStatus;
  };

  const translateStatus = (status) => {
    const translations = {
      pending: "en attente",
      processing: "en cours de traitement",
      shipped: "expédié",
      completed: "terminé",
      cancelled: "annuler",
    };
    return translations[status] || status;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "en attente":
        return "success";
      case "en cours de traitement":
        return "info";
      case "expédié":
        return "primary";
      case "terminé":
        return "warning";
      case "Annuler":
        return "danger";
      default:
        return "secondary";
    }
  };

  const columns = [
    {
      header: "Nom",
      accessorKey: "user",
      Cell: ({ cell }) => (
        <span>
          {cell.row.original.user?.firstName || ""}{" "}
          {cell.row.original.user?.lastName || ""}
        </span>
      ),
    },
    {
      header: "Total",
      accessorKey: "total",
      Cell: ({ cell }) => `${cell.getValue()?.toFixed(3)} TND`,
    },
    {
      header: "Statut",
      accessorKey: "status",
      Cell: ({ cell }) => (
        <span className={`badge bg-${getStatusBadgeColor(cell.getValue())}`}>
          {translateStatus(cell.getValue())}
        </span>
      ),
    },
    {
      header: "Téléphone",
      accessorKey: "phoneNumber",
    },
    {
      accessorKey: "id",
      header: "Détails",
      Cell: ({ cell }) => {
        const order = cell.row.original;
        return (
          <div className="d-flex gap-2">
            <div className="actions-right block_action">
              <Button
                onClick={() => navigate.push("/orders/detail/" + order.id)}
                variant="info"
                size="sm"
                className="text-info btn-link view"
              >
                <i className="fa fa-eye" />
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "change",
      header: "Actions",
      Cell: ({ cell }) => {
        const order = cell.row.original;
        const nextStatus = getNextStatus(order.status);
        const isProcessing = loadingStates[order.id];

        return (
          <div className="d-flex gap-2">
            <div className="actions-right block_action">
              {nextStatus && nextStatus !== order.status && (
                <Button
                  onClick={() => updateOrderStatus(order.id, nextStatus)}
                  variant="success"
                  size="sm"
                  disabled={isProcessing}
                >
                  {isProcessing ? "Traitement..." : translateStatus(nextStatus)}
                </Button>
              )}

              <Button
                onClick={() => updateOrderStatus(order.id, "Annuler")}
                variant="danger"
                size="sm"
                disabled={isProcessing}
              >
                Annuler
              </Button>
            </div>
          </div>
        );
      },
    },
  ];

  const getOrders = useCallback(async () => {
    try {
      const response = await dispatch(fetchOrders({ isBulk: 0 }));
      setEntities(response.payload);
    } catch (error) {
      notify(2, "Échec du chargement des commandes");
    }
  }, [dispatch]);

  useEffect(() => {
    getOrders();
  }, [getOrders]);

  return (
    <>
      <Container fluid>
        <ToastContainer />
        <br />

        <Row>
          <Col md="12">
            <h4 className="title">Liste des Commandes</h4>
            <Card>
              <Card.Header>
                <Nav
                  variant="tabs"
                  activeKey={activeTab}
                  onSelect={setActiveTab}
                >
                  {STATUS_TABS.map((tab) => (
                    <Nav.Item key={tab.id}>
                      <Nav.Link eventKey={tab.id}>{tab.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Card.Header>
              <Card.Body>
                <MaterialReactTable
                  columns={columns}
                  data={filteredEntities}
                  enableColumnActions
                  enableColumnFilters
                  enablePagination
                  enableSorting
                  enableBottomToolbar
                  enableTopToolbar
                  muiTableBodyRowProps={{ hover: false }}
                  localization={{
                    noRecordsToDisplay: "Aucune commande trouvée",
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ListOrders;
